import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import type { ComponentProps, ReactNode } from "react";
import * as React from "react";

import type { AppIcon } from "@/components/appIcons";
import { Link, type LinkTarget } from "@/components/link";
import { cn } from "@/lib/utils";

import { ScrollFade } from "./scroll-fade";

interface ToggleGroupContextValue {
  value: readonly string[];
  toggle: (value: string) => void;
}

const ToggleGroupContext = React.createContext<ToggleGroupContextValue | null>(null);

function useToggleGroupContext(component: string) {
  const ctx = React.useContext(ToggleGroupContext);
  if (!ctx) throw new Error(`${component} must be used inside <ToggleGroupRoot>`);
  return ctx;
}

type ToggleGroupRootProps = Omit<ComponentProps<"div">, "onChange"> & {
  value?: readonly string[];
  defaultValue?: readonly string[];
  onValueChange?: (value: string[]) => void;
  wrapperClassName?: string;
  /** Show a mobile "scroll right" button while options overflow. */
  scrollHint?: boolean;
};

function ToggleGroupRoot({
  className,
  wrapperClassName,
  scrollHint,
  value,
  defaultValue,
  onValueChange,
  ...props
}: ToggleGroupRootProps) {
  const [internal, setInternal] = React.useState<readonly string[]>(defaultValue ?? []);
  const isControlled = value !== undefined;
  const current = isControlled ? value : internal;

  const toggle = React.useCallback(
    (v: string) => {
      // Single-select replace (matches Base UI default): clicking the current
      // value deselects; clicking another replaces.
      const next = current.includes(v) ? [] : [v];
      if (!isControlled) setInternal(next);
      onValueChange?.(next);
    },
    [current, isControlled, onValueChange],
  );

  const ctx = React.useMemo<ToggleGroupContextValue>(
    () => ({ value: current, toggle }),
    [current, toggle],
  );

  return (
    <ToggleGroupContext.Provider value={ctx}>
      <ScrollFade className={wrapperClassName} scrollHint={scrollHint}>
        <div role="group" className={className} {...props} />
      </ScrollFade>
    </ToggleGroupContext.Provider>
  );
}

type ToggleGroupVariant = "pill" | "chip" | "segment" | "tab";

/** Track classes for a `segment` group (pass as the ToggleGroupRoot className). */
/** Same height and border as the other filter controls (h-11 on phones, h-10 from `sm`). Phones use tighter padding, and each segment item's ::after reaches the track edge so the whole 44px height is tappable. */
export const TOGGLE_SEGMENT_TRACK =
  "flex h-11 items-stretch gap-0.5 rounded-md border border-border bg-secondary/50 p-1 max-sm:p-0.5 sm:h-10";

const BADGE_STYLES: Record<ToggleGroupVariant, string> = {
  pill: "px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground group-data-[pressed]:bg-primary/10 group-data-[pressed]:text-primary",
  chip: "px-1.5 py-0.5 rounded-full bg-muted group-data-[pressed]:bg-primary-foreground group-data-[pressed]:text-primary",
  segment: "text-muted-foreground group-data-[pressed]:text-primary-foreground/75",
  tab: "font-medium text-muted-foreground",
};

const toggleGroupItemVariants = cva(
  "group flex shrink-0 items-center gap-1.5 whitespace-nowrap text-sm font-medium transition-all cursor-pointer select-none outline-none disabled:cursor-not-allowed disabled:opacity-40",
  {
    variants: {
      variant: {
        pill: "px-3 py-1.5 rounded-lg max-sm:min-h-11",
        chip: "px-3 py-1.5 rounded-full max-sm:min-h-11",
        /** Segmented control item; wrap the group in `TOGGLE_SEGMENT_TRACK`. */
        segment:
          "relative justify-center px-3 rounded-[5px] font-semibold after:absolute after:inset-x-0 after:-inset-y-[3px] after:content-['']",
        /** Underlined tab, for view switches such as release types. */
        tab: "h-11 sm:h-10 rounded-none border-b-2 px-0.5 font-semibold",
      },
    },
    defaultVariants: { variant: "pill" },
  },
);

const pressedStyles = {
  pill: {
    pressed: "bg-secondary text-foreground",
    unpressed: "text-muted-foreground hover:text-foreground hover:bg-secondary/50",
  },
  chip: {
    pressed: "bg-primary text-primary-foreground border border-primary",
    unpressed:
      "bg-secondary text-muted-foreground border border-border hover:bg-secondary/80 hover:text-foreground",
  },
  segment: {
    pressed: "bg-primary text-primary-foreground",
    unpressed: "text-foreground/80 hover:bg-secondary hover:text-foreground",
  },
  tab: {
    pressed: "border-primary text-foreground",
    unpressed: "border-transparent text-muted-foreground hover:text-foreground",
  },
};

type ToggleGroupItemProps = Omit<ComponentProps<"button">, "value"> &
  VariantProps<typeof toggleGroupItemVariants> & {
    value: string;
    icon?: AppIcon;
    badge?: ReactNode;
    children?: ReactNode;
  };

type ToggleGroupLinkItemProps = Omit<ComponentProps<"a">, "href"> &
  VariantProps<typeof toggleGroupItemVariants> & {
    value: string;
    icon?: AppIcon;
    badge?: ReactNode;
    children?: ReactNode;
    link: LinkTarget;
  };

type ToggleGroupOptionItemProps = VariantProps<typeof toggleGroupItemVariants> & {
  value: string;
  icon?: AppIcon;
  badge?: ReactNode;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  link?: LinkTarget;
};

function getToggleGroupItemClassName({
  className,
  pressed,
  variant,
}: {
  className?: string;
  pressed: boolean;
  variant: ToggleGroupVariant;
}) {
  const styles = pressedStyles[variant];
  return cn(
    toggleGroupItemVariants({ variant }),
    pressed ? styles.pressed : styles.unpressed,
    className,
  );
}

function ToggleGroupItemContent({
  badge,
  children,
  icon,
  variant,
}: {
  badge?: ReactNode;
  children?: ReactNode;
  icon?: AppIcon;
  variant: ToggleGroupVariant;
}) {
  const Icon = icon;

  return (
    <>
      {Icon ? <Icon className="text-[16px]" /> : null}
      {children}
      {badge !== undefined && (
        <span className={cn("text-xs tabular-nums font-semibold", BADGE_STYLES[variant])}>
          {badge}
        </span>
      )}
    </>
  );
}

function ToggleGroupItem({
  className,
  variant = "pill",
  icon,
  badge,
  children,
  value,
  onClick,
  ...props
}: ToggleGroupItemProps) {
  const ctx = useToggleGroupContext("ToggleGroupItem");
  const pressed = ctx.value.includes(value);
  const resolvedVariant = variant ?? "pill";

  return (
    <button
      type="button"
      aria-pressed={pressed}
      data-pressed={pressed ? "" : undefined}
      className={getToggleGroupItemClassName({
        className,
        pressed,
        variant: resolvedVariant,
      })}
      onClick={(e) => {
        onClick?.(e);
        if (!e.defaultPrevented) ctx.toggle(value);
      }}
      {...props}
    >
      <ToggleGroupItemContent icon={icon} badge={badge} variant={resolvedVariant}>
        {children}
      </ToggleGroupItemContent>
    </button>
  );
}

function ToggleGroupLinkItem({
  className,
  variant = "pill",
  icon,
  badge,
  children,
  value,
  link,
  // Toggle links are filter variants of the current page, not pages worth crawling.
  rel = "nofollow",
  ...props
}: ToggleGroupLinkItemProps) {
  const ctx = useToggleGroupContext("ToggleGroupLinkItem");
  const pressed = ctx.value.includes(value);
  const resolvedVariant = variant ?? "pill";

  return (
    <Link
      {...link}
      rel={rel}
      resetScroll={false}
      aria-current={pressed ? "page" : undefined}
      data-pressed={pressed ? "" : undefined}
      className={getToggleGroupItemClassName({
        className,
        pressed,
        variant: resolvedVariant,
      })}
      {...props}
    >
      <ToggleGroupItemContent icon={icon} badge={badge} variant={resolvedVariant}>
        {children}
      </ToggleGroupItemContent>
    </Link>
  );
}

function ToggleGroupOptionItem({ link, disabled, ...props }: ToggleGroupOptionItemProps) {
  if (link && !disabled) {
    return <ToggleGroupLinkItem {...props} link={link} />;
  }

  return <ToggleGroupItem {...props} disabled={disabled} />;
}

export {
  ToggleGroupRoot,
  ToggleGroupItem,
  ToggleGroupLinkItem,
  ToggleGroupOptionItem,
  toggleGroupItemVariants,
};
